import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { isInactivePayslipExceptionEmail } from '../../../../utils/payrollUtils';

const PayslipSummaryCard = ({
  mode,
  employee,
  payrollEmployeeData,
  calculatedPayslip,
  attendanceAnalytics,
  attendanceFilters,
  holidaysApplied,
  additionalIncome,
  additionalDeductions,
  payslipDeductions,
  loanSummary,
  payrollPeriod,
  otHours = 0,
  holidayHours = 0,
  onOpenPayslip,
  onOpenRecords,
  onOpenCalendar,
  capturedValuesRef
}) => {
  const isMF = mode === 'mf';
  const isInactivePayslipException = isInactivePayslipExceptionEmail(
    employee?.email || payrollEmployeeData?.email || calculatedPayslip?.employeeEmail || ''
  );
  const dailyRate = calculatedPayslip?.dailyRate || 0;
  const minuteRate = dailyRate / 8 / 60;

  const otAmount = (dailyRate / 8) * 1.25 * otHours;
  const holidayAmount = (dailyRate / 8) * 1.3 * holidayHours;

  const absenceDays = isInactivePayslipException ? 0 : (attendanceAnalytics?.days_absent || 0);
  const lateMinutes = isInactivePayslipException ? 0 : (attendanceAnalytics?.total_minutes_late || 0);
  const undertimeMinutes = isInactivePayslipException ? 0 : (attendanceAnalytics?.total_minutes_undertime || 0);
  const base =
    isMF ? (parseFloat(payrollEmployeeData?.allowances?.mf) || 0) : ((calculatedPayslip?.basicSalary || 0) / 2);
  const basicComputed = isInactivePayslipException
    ? base
    : (base - (absenceDays * dailyRate) - (lateMinutes * minuteRate) - (undertimeMinutes * minuteRate));

  const additionalIncomeWithAmounts = (additionalIncome || []).map((inc) => {
    const unit = inc.unit || 'minutes';
    let amount = 0;
    if (unit === 'amount') amount = parseFloat(inc.amount) || 0;
    else if (unit === 'days') amount = (parseFloat(inc.days) || 0) * dailyRate;
    else amount = minuteRate * (parseFloat(inc.minutes) || 0);
    return { ...inc, amount };
  });
  const additionalIncomeTotal = additionalIncomeWithAmounts.reduce((sum, inc) => sum + (inc.amount || 0), 0);
  const additionalDeductionsWithAmounts = (additionalDeductions || []).map((ded) => {
    const unit = ded.unit || 'minutes';
    let amount = 0;
    if (unit === 'amount') amount = parseFloat(ded.amount) || 0;
    else if (unit === 'days') amount = (parseFloat(ded.days) || 0) * dailyRate;
    else amount = minuteRate * (parseFloat(ded.minutes) || 0);
    return { ...ded, amount };
  });
  const additionalDeductionsTotal = additionalDeductionsWithAmounts.reduce((sum, ded) => sum + (ded.amount || 0), 0);

  const grossSalary = basicComputed + additionalIncomeTotal;
  const sssContribution = payslipDeductions?.sss ? (parseFloat(calculatedPayslip?.deductions?.sss) || 0) : 0;
  const hdmfContribution = payslipDeductions?.hdmf ? (parseFloat(calculatedPayslip?.deductions?.hdmf) || 0) : 0;
  const philhealthContribution = payslipDeductions?.phic ? (parseFloat(calculatedPayslip?.deductions?.phic) || 0) : 0;
  const withholdingTax = payslipDeductions?.tax ? (parseFloat(calculatedPayslip?.deductions?.tax) || 0) : 0;
  const totalGovDeductions = sssContribution + hdmfContribution + philhealthContribution + withholdingTax;
  const totalDeductions = totalGovDeductions + additionalDeductionsTotal;
  const loanDeduction = !isMF ? (Number(loanSummary?.loanDeductionPerCutoffTotal) || 0) : 0;
  const hasLoanDetail = loanDeduction > 0;
  const netPay = Math.max(0, grossSalary + otAmount + holidayAmount - totalDeductions - (hasLoanDetail ? loanDeduction : 0));

  const startDate = attendanceFilters?.start_date;
  const endDate = attendanceFilters?.end_date;

  if (capturedValuesRef && typeof capturedValuesRef === 'object') {
    capturedValuesRef.current = {
      isMF,
      dailyRate, minuteRate,
      otHours, holidayHours, otAmount, holidayAmount,
      absenceDays, lateMinutes, undertimeMinutes,
      halfMonthSalary: base,
      absenceDeduction: absenceDays * dailyRate,
      tardinessDeduction: lateMinutes * minuteRate,
      undertimeDeduction: undertimeMinutes * minuteRate,
      basicComputed, grossSalary,
      sssContribution, hdmfContribution, philhealthContribution, withholdingTax,
      totalGovDeductions, totalDeductions,
      additionalIncomeWithAmounts, additionalIncomeTotal,
      additionalDeductionsWithAmounts, additionalDeductionsTotal,
      loanDeduction, hasLoanDetail, netPay,
      holidays: Array.isArray(holidaysApplied) ? holidaysApplied : [],
      loanRows: Array.isArray(loanSummary?.rows) ? loanSummary.rows : [],
      loanFormTypes: Array.isArray(loanSummary?.loanFormTypes) ? loanSummary.loanFormTypes : [],
      loanBreakdown: (loanSummary?.breakdown && typeof loanSummary.breakdown === 'object') ? loanSummary.breakdown : {}
    };
  }

  return (
    <Card className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-indigo-800">Payslip Calculation Summary</CardTitle>
        </div>
      </CardHeader>
      <div className="px-4 pb-2 flex flex-wrap gap-2">
        <Button variant="outline" onClick={onOpenPayslip}>Payroll Payslip</Button>
        <Button variant="outline" onClick={onOpenRecords}>Detailed Attendance Records</Button>
        <Button variant="outline" onClick={onOpenCalendar}>Calendar View</Button>
      </div>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg border border-indigo-200">
            <h3 className="font-bold text-gray-800 mb-2">Employee Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="font-medium">Name:</span> {employee?.full_name}</div>
              <div><span className="font-medium">Branch:</span> {(employee?.branch || payrollEmployeeData?.branch) || 'N/A'}</div>
              <div><span className="font-medium">TIN:</span> {payrollEmployeeData?.tin_number || 'N/A'}</div>
              <div><span className="font-medium">SSS:</span> {payrollEmployeeData?.sss_number || 'N/A'}</div>
              <div><span className="font-medium">PHIC:</span> {payrollEmployeeData?.phic_number || 'N/A'}</div>
              <div><span className="font-medium">HDMF:</span> {payrollEmployeeData?.hdmf_number || 'N/A'}</div>
              <div><span className="font-medium">Period:</span> {payrollPeriod || 'Not set'}</div>
              <div><span className="font-medium">Dates:</span> {startDate} to {endDate}</div>
            </div>
          </div>

          <div className={`grid grid-cols-1 ${isMF ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-green-800">Income Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {(holidaysApplied || []).length > 0 && (
                  <div className="border-b pb-2 mb-2">
                    <p className="text-xs font-semibold text-gray-700 mb-1">Holidays:</p>
                    <div className="space-y-1">
                      {(holidaysApplied || []).map((h, idx) => (
                        <div key={`${h?.date || 'holiday'}_${idx}`} className="text-xs text-gray-700">
                          {String(h?.date || '')}{h?.name ? ` — ${String(h?.name)}` : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>{isMF ? 'MF Amount:' : 'Half Month Salary:'}</span>
                  <span className="font-medium">
                    ₱{(isMF ? (parseFloat(payrollEmployeeData?.allowances?.mf) || 0) : ((calculatedPayslip?.basicSalary || 0) / 2)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                </div>
                {!isInactivePayslipException && (
                  <div className="border-t pt-2">
                    <p className="text-xs font-semibold text-red-700 mb-1">Less:</p>
                    <div className="flex justify-between pl-2 text-xs">
                      <span>Absence ({absenceDays} days):</span>
                      <span className="text-red-600">-₱{(absenceDays * dailyRate).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between pl-2 text-xs">
                      <span>Tardiness ({lateMinutes} mins):</span>
                      <span className="text-red-600">-₱{(lateMinutes * minuteRate).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between pl-2 text-xs">
                      <span>Undertime ({undertimeMinutes} mins):</span>
                      <span className="text-red-600">-₱{(undertimeMinutes * minuteRate).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  </div>
                )}
                {(additionalIncome || []).length > 0 && (
                  <div className="border-t pt-2">
                    <p className="text-xs font-semibold text-green-700 mb-1">Additional Income:</p>
                    {(additionalIncome || []).map((inc) => {
                      const unit = inc.unit || 'minutes';
                      let amount = 0;
                      if (unit === 'amount') amount = parseFloat(inc.amount) || 0;
                      else if (unit === 'days') amount = (parseFloat(inc.days) || 0) * dailyRate;
                      else amount = minuteRate * (parseFloat(inc.minutes) || 0);
                      return (
                        <div key={inc.id} className="flex justify-between pl-2 text-xs">
                          <span>{inc.title}:</span>
                          <span className="text-green-600">+₱{amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex justify-between font-bold pt-2 border-t-2 text-base text-green-700">
                  <span>Gross Salary:</span>
                  <span>₱{grossSalary.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              </CardContent>
            </Card>

            {!isMF && (
              <Card className="bg-red-50 border-red-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-red-800">Deductions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p className="text-xs font-semibold text-red-700 mb-2">Government Deductions:</p>
                  {payslipDeductions?.sss && (
                    <div className="flex justify-between pl-2">
                      <span>SSS:</span>
                      <span className="text-red-600">-₱{((calculatedPayslip?.deductions?.sss) || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  )}
                  {payslipDeductions?.hdmf && (
                    <div className="flex justify-between pl-2">
                      <span>HDMF:</span>
                      <span className="text-red-600">-₱{(calculatedPayslip?.deductions?.hdmf || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  )}
                  {payslipDeductions?.phic && (
                    <div className="flex justify-between pl-2">
                      <span>PhilHealth:</span>
                      <span className="text-red-600">-₱{(calculatedPayslip?.deductions?.phic || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  )}
                  {payslipDeductions?.tax && (
                    <div className="flex justify-between pl-2">
                      <span>Withholding Tax:</span>
                      <span className="text-red-600">-₱{(calculatedPayslip?.deductions?.tax || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  )}
                  {(additionalDeductions || []).length > 0 && (
                    <div className="border-t pt-2 mt-2">
                      <p className="text-xs font-semibold text-red-700 mb-1">Additional Deductions:</p>
                      {(additionalDeductions || []).map((ded) => {
                        const unit = ded.unit || 'minutes';
                        let amount = 0;
                        if (unit === 'amount') amount = parseFloat(ded.amount) || 0;
                        else if (unit === 'days') amount = (parseFloat(ded.days) || 0) * dailyRate;
                        else amount = minuteRate * (parseFloat(ded.minutes) || 0);
                        return (
                          <div key={ded.id} className="flex justify-between pl-2 text-xs">
                            <span>{ded.title}:</span>
                            <span className="text-red-600">-₱{amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-2 border-t-2 text-red-700">
                    <span>Total Deductions:</span>
                    <span>₱{totalDeductions.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {!isMF && (
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300 border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-blue-800">Net Pay</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Gross Salary:</span>
                      <span className="font-medium">
                        ₱{grossSalary.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                    {hasLoanDetail && (
                      <div className="flex justify-between text-sm">
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Total Deductions:</span>
                      <span className="font-medium text-red-600">
                        ₱{totalDeductions.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                    {hasLoanDetail && (
                      <div className="flex justify-between">
                        <span className="font-medium text-slate-700">
                          {(Array.isArray(loanSummary?.loanFormTypes) ? loanSummary.loanFormTypes : []).join(', ') || '—'}
                        </span>
                        <span className="font-medium text-red-600">
                          -₱{loanDeduction.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                    )}
                    {otHours > 0 && (
                      <div className="flex justify-between">
                        <span>OT Pay ({otHours} hrs):</span>
                        <span className="font-medium text-green-600">
                          +₱{otAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                    )}
                    {holidayHours > 0 && (
                      <div className="flex justify-between">
                        <span>Holiday Pay ({holidayHours} hrs):</span>
                        <span className="font-medium text-green-600">
                          +₱{holidayAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="border-t-2 border-blue-300 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-blue-900 text-lg">NET PAY:</span>
                      <span className="font-bold text-green-700 text-2xl">
                        ₱{netPay.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg mt-3">
                    <p className="text-xs text-blue-700 text-center">
                      ⚠️ Make sure to set Payroll Period in the Payslip modal before saving
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PayslipSummaryCard;
